import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { 
  Users,
  Phone,
  Mail,
  Calendar,
  Clock,
  AlertTriangle,
  Scissors,
  Palette,
  Sparkles,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Send,
  User,
  CheckCircle2
} from "lucide-react";

export default function ClientiDaRichiamare({ onInviaMessaggio }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dati, setDati] = useState(null);
  const [serviziEspansi, setServiziEspansi] = useState({});

  useEffect(() => {
    fetchClientiDaRichiamare();
  }, []);

  const fetchClientiDaRichiamare = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/marketing/clienti-da-richiamare-servizi`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDati(res.data);
      
      // Espandi il primo servizio di default
      if (res.data?.clienti_per_servizio) {
        const primoServizio = Object.keys(res.data.clienti_per_servizio)[0];
        if (primoServizio) {
          setServiziEspansi({ [primoServizio]: true });
        }
      }
    } catch (error) {
      toast.error("Errore nel caricamento clienti da richiamare");
    } finally {
      setLoading(false);
    }
  };

  const toggleServizio = (nomeServizio) => {
    setServiziEspansi(prev => ({
      ...prev,
      [nomeServizio]: !prev[nomeServizio]
    }));
  };

  const getIconaServizio = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case 'colore': return <Palette className="w-4 h-4 text-pink-500" />;
      case 'taglio': return <Scissors className="w-4 h-4 text-blue-500" />;
      case 'trattamento': return <Sparkles className="w-4 h-4 text-purple-500" />;
      default: return <Scissors className="w-4 h-4 text-slate-500" />;
    }
  };

  const getPrioritaColor = (priorita) => {
    switch (priorita) {
      case 'alta': return 'bg-red-100 text-red-700 border-red-200';
      case 'media': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const formatData = (dataStr) => {
    try {
      return format(parseISO(dataStr), "d MMM yyyy", { locale: it });
    } catch {
      return dataStr;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          <p className="mt-2 text-slate-500">Caricamento...</p>
        </CardContent>
      </Card>
    );
  }

  if (!dati || !dati.servizi_configurati || dati.servizi_configurati.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-purple-500" />
            Clienti da Richiamare
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-400 mb-3" />
            <p className="font-medium">Nessun servizio configurato</p>
            <p className="text-sm mt-1">
              Vai in <strong>Servizi</strong> e imposta i "Giorni Richiamo" per ogni servizio
              <br />
              (es. Colore Radici = 30 giorni, Taglio = 60 giorni)
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totaleClienti = Object.values(dati.clienti_per_servizio || {})
    .reduce((acc, s) => acc + (s.totale_da_richiamare || 0), 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-purple-500" />
            Clienti da Richiamare
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-purple-50 text-purple-700">
              {totaleClienti} clienti
            </Badge>
            <Button variant="ghost" size="sm" onClick={fetchClientiDaRichiamare}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Basato sulle tempistiche configurate in ogni servizio
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Servizi configurati */}
        <div className="flex flex-wrap gap-2 pb-2 border-b">
          {dati.servizi_configurati.map(s => (
            <Badge key={s.id} variant="outline" className="text-xs">
              {getIconaServizio(s.tipo)}
              <span className="ml-1">{s.nome}</span>
              <span className="ml-1 text-slate-400">({s.giorni_richiamo}gg)</span>
            </Badge>
          ))}
        </div>

        {/* Lista per servizio */}
        {Object.entries(dati.clienti_per_servizio || {}).map(([nomeServizio, info]) => (
          <div key={nomeServizio} className="border rounded-lg overflow-hidden">
            {/* Header servizio - cliccabile */}
            <button
              onClick={() => toggleServizio(nomeServizio)}
              className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                {getIconaServizio(info.tipo)}
                <span className="font-medium">{nomeServizio}</span>
                <Badge variant="outline" className="text-xs">
                  ogni {info.giorni_richiamo}gg
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={info.totale_da_richiamare > 10 ? 'bg-red-500' : info.totale_da_richiamare > 5 ? 'bg-amber-500' : 'bg-emerald-500'}>
                  {info.totale_da_richiamare} da richiamare
                </Badge>
                {serviziEspansi[nomeServizio] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </button>

            {/* Lista clienti - espandibile */}
            {serviziEspansi[nomeServizio] && (
              <div className="divide-y max-h-64 overflow-y-auto">
                {info.clienti.map(cliente => (
                  <div key={cliente.cliente_id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {cliente.nome} {cliente.cognome}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatData(cliente.ultima_data)}
                          </span>
                          <Badge variant="outline" className={`text-xs ${getPrioritaColor(cliente.priorita)}`}>
                            {cliente.giorni_passati}gg fa
                          </Badge>
                          {cliente.giorni_ritardo > 0 && (
                            <span className="text-red-500">+{cliente.giorni_ritardo}gg ritardo</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {cliente.telefono && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => window.open(`tel:${cliente.telefono}`)}
                          title={cliente.telefono}
                        >
                          <Phone className="w-4 h-4 text-emerald-500" />
                        </Button>
                      )}
                      {cliente.email && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => window.open(`mailto:${cliente.email}`)}
                          title={cliente.email}
                        >
                          <Mail className="w-4 h-4 text-blue-500" />
                        </Button>
                      )}
                      {cliente.telefono && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => onInviaMessaggio?.(cliente, nomeServizio)}
                          title="Invia WhatsApp"
                        >
                          <MessageSquare className="w-4 h-4 text-green-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Se non ci sono clienti da richiamare */}
        {Object.keys(dati.clienti_per_servizio || {}).length === 0 && (
          <div className="text-center py-6 text-slate-500">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-2" />
            <p className="font-medium">Tutti i clienti sono in regola!</p>
            <p className="text-sm">Nessun cliente da richiamare al momento</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
